import { PrimaryTemplate } from 'components/templates/PrimaryTemplate';
import { useTranslation } from 'react-i18next';
import styles from './SiteGroupManagement.module.scss';
import DOMPurify from 'dompurify';
import { IconPlusCircle } from 'components/atoms/icons/IconPlusCircle';
import { IconEditIC } from 'components/atoms/icons/IconEditIC';
import DeleteItemDarkModal from 'components/molecules/DeleteItemDarkModal';
import { IconDelete } from 'components/atoms/icons/IconDelete';
import { ModalType } from 'util/Enums';
import { FormEvent, useEffect, useRef, useState } from 'react';
import { createAxios } from 'ts/createAxios';
import { handleError } from 'util/errorHandler';
import { ExpandableList } from 'components/organisms/ExpandableList';
import CreateUpdateSiteGroupModal, {
  SiteGroup,
  SiteGroupCreateEditModalType,
} from 'components/organisms/CreateUpdateSiteGroupModal';
import { IconSearch } from 'components/atoms/icons/IconSearch';
import CustomPagination from 'components/atoms/CustomPagination';
import { DEFAULT_PAGE } from 'util/ConstantValues';
import { SiteGroupInput } from 'components/molecules/CreateUpdateSiteGroupForm';
import { toast } from 'react-toastify';
import { mergeClasses } from 'util/commons';

const RECORDS_OF_PAGE = 100;

interface FetchSiteGroupRes {
  groupSites: SiteGroup[];
  page: number;
  total: number;
}

type SiteGroupFilter = {
  page: number;
  take: number;
  keyword: string;
};

const SiteGroupManagement = () => {
  const { t } = useTranslation();
  const api = createAxios();
  const [modalAddEdit, setModalData] = useState<
    SiteGroupCreateEditModalType | undefined
  >();
  const [data, setData] = useState<FetchSiteGroupRes>({
    groupSites: [],
    page: DEFAULT_PAGE,
    total: 0,
  });
  const [deleteItem, setDeleteItem] = useState<SiteGroup | undefined>();
  const inputRef = useRef<HTMLInputElement>(null);
  const [filter, setFilter] = useState<SiteGroupFilter>({
    page: DEFAULT_PAGE,
    take: RECORDS_OF_PAGE,
    keyword: '',
  });

  const fetchSiteGroups = (params: SiteGroupFilter) => {
    api
      .get<FetchSiteGroupRes>(`/group-sites`, { params })
      .then(async (res) => {
        const data = res.data;

        setData({
          groupSites: data.groupSites,
          page: data.page,
          total: data.total,
        });
      })
      .catch((e) => {
        handleError(e);
      });
  };

  const handleCreate = (data: SiteGroupInput) => {
    api
      .post('/group-sites', {
        name: data.name.trim(),
        siteIds: data.siteIds || [],
      })
      .then(() => {
        fetchSiteGroups(filter);
        toast.success(t('messages.M_010'));
        setModalData(undefined);
      })
      .catch((e) => {
        handleError(e);
      });
  };

  const handleEdit = (data: SiteGroupInput, id: number) => {
    api
      .put(`/group-sites/${id}`, {
        name: data.name.trim(),
        siteIds: data.siteIds || [],
      })
      .then(() => {
        fetchSiteGroups(filter);
        toast.success(t('messages.M_011'));
        setModalData(undefined);
      })
      .catch((e) => {
        handleError(e);
      });
  };

  const handleDelete = (id?: string | number) => {
    api
      .delete(`/group-sites/${id}`)
      .then(() => {
        fetchSiteGroups(filter);
        toast.success(t('messages.M_012'));
        setDeleteItem(undefined);
      })
      .catch((e) => {
        handleError(e);
      });
  };

  const handleSearch = (e: FormEvent) => {
    e.preventDefault();
    const value = inputRef.current?.value || '';

    setFilter((prev) => ({ ...prev, page: DEFAULT_PAGE, keyword: value }));
  };

  useEffect(() => {
    fetchSiteGroups(filter);
  }, [filter]);

  return (
    <PrimaryTemplate
      h1={t('site_group_management.heading')}
      className={styles.siteGroup}
    >
      <div className={styles.siteGroupContent}>
        <div className={styles.title}>
          <div className={styles.leftBar}>
            <form className={styles.searchWrapper}>
              <div className={styles.searchBox}>
                <IconSearch />
                <input
                  placeholder={t('site_group_management.label_name') || ''}
                  ref={inputRef}
                  name="keyword"
                />
              </div>
              <button
                className={styles.buttonSearch}
                type="submit"
                onClick={(e: FormEvent) => handleSearch(e)}
              >
                {t('site_group_management.search')}
              </button>
            </form>
          </div>

          <div className={styles.rightBar}>
            <div
              className={styles.countItem}
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(
                  t('common.page', {
                    page: data.total,
                  }) ?? ''
                ),
              }}
            />
            <button
              className={styles.submitButton}
              onClick={() => {
                setModalData({
                  type: ModalType.add,
                });
              }}
            >
              <div className={styles.iconAdd}>
                <IconPlusCircle />
              </div>
              <span>{t('site_group_management.add')}</span>
            </button>
          </div>
        </div>
        <div
          className={mergeClasses(styles.table, {
            [styles.tableWithPagination]: data.total > RECORDS_OF_PAGE,
          })}
        >
          <table id={styles.siteGroupTable}>
            <thead>
              <tr>
                <th className={styles.groupName}>
                  {t('site_group_management.label_name')}
                </th>
                <th className={styles.sites}>
                  {t('site_group_management.label_site')}
                </th>
                <th colSpan={2} className={styles.actionField}>
                  {t('site_group_management.label_action')}
                </th>
              </tr>
            </thead>
            <tbody>
              {data.groupSites && data.groupSites.length > 0 ? (
                data.groupSites.map((item, i) => {
                  const sites: string[] = item.sites
                    .map((tenant) => tenant.name)
                    .sort();

                  return (
                    <tr key={i}>
                      <td className={styles.groupName}>
                        <p title={item.name}>{item.name}</p>
                      </td>
                      <td className={styles.sites}>
                        <ExpandableList data={sites} />
                      </td>
                      <td className={styles.actionField1}>
                        <div className={styles.contentAction}>
                          <div
                            className={styles.actionEdit}
                            onClick={() => {
                              setModalData({
                                type: ModalType.update,
                                group: item,
                              });
                            }}
                          >
                            <IconEditIC />
                          </div>
                        </div>
                      </td>
                      <td className={styles.actionField1}>
                        <div className={styles.contentAction}>
                          <div
                            className={styles.actionDelete}
                            onClick={() => {
                              setDeleteItem(item);
                            }}
                          >
                            <IconDelete />
                          </div>
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={3} className={styles.msgError}>
                    {t('messages.M_008')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className={styles.pagination}>
          {data.total > RECORDS_OF_PAGE && (
            <CustomPagination
              pageCount={Math.ceil(data.total / RECORDS_OF_PAGE)}
              onPageChange={({ selected }) => {
                setFilter((prev) => ({ ...prev, page: selected + 1 }));
              }}
              forcePage={data.page - 1}
            />
          )}
        </div>
      </div>

      {modalAddEdit && (
        <CreateUpdateSiteGroupModal
          onClose={() => {
            setModalData(undefined);
          }}
          groupData={modalAddEdit}
          onSubmit={(data, id) => {
            if (id) {
              handleEdit(data, id);
            } else {
              handleCreate(data);
            }
          }}
        />
      )}
      {deleteItem && (
        <DeleteItemDarkModal
          deletingId={deleteItem.id}
          deletingName={deleteItem.name}
          onClose={() => {
            setDeleteItem(undefined);
          }}
          onHandleDelete={handleDelete}
        />
      )}
    </PrimaryTemplate>
  );
};

export default SiteGroupManagement;
